import React, { createContext, useContext, useState } from "react";
import {
  IFoodItem,
  IFoodMenuCategory,
  ISubCategoriesJson,
} from "../../../../../redux/food/food.types";
import { useBillingCompanyContext } from "../../../../../providers";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { fetchFoodMenuCategoryListWithFoodItemsAsync } from "../../../../../redux/food/foodActions";

interface IFoodMenuContext {
  allCategories: IFoodMenuCategory[];

  selectedCategory: IFoodMenuCategory | null;
  onCategorySelect: (category: IFoodMenuCategory | null) => void;

  categorySearchText: string;
  setCategorySearchText: (text: string) => void;

  selectedSubCategory: ISubCategoriesJson | null;
  onSubCategorySelect: (
    category: IFoodMenuCategory,
    subCategory: ISubCategoriesJson | null,
  ) => void;

  selectedItems: IFoodItem[];
  addItemToOrder: (item: IFoodItem) => void;
  removeItemFromOrder: (itemUuid: string) => void;
  updateItemQuantity: (itemUuid: string, quantity: number) => void;
  clearOrder: () => void;
  currentCategoryItems: IFoodItem[];
  // setCurrentCategoryItems: (items: IFoodItem[]) => void;
  fetchedItems: IFoodItem[];
  setFetchedItems: (items: IFoodItem[]) => void;
  masterMenusFetchedItems: IFoodItem[];
  setMasterMenusFetchedItems: (items: IFoodItem[]) => void;
  removeFetchedItem: (itemUuid: string) => void;
  deletedItemUuids: Set<string>;
  addDeletedItemUuid: (uuid: string) => void;
  mergeItemsFromMultipleMenus: (itemsArrays: IFoodItem[][]) => IFoodItem[];
  categoryLimits: Record<string, number>;
  setCategoryLimits: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  menuPrice: number;
  setMenuPrice: (price: number) => void;
  updateItemSelectionShow: (itemUuid: string, isSelected: number, isShow: number) => void;
}

const OrderContext = createContext<IFoodMenuContext | undefined>(undefined);

export const OrderProvider: React.FC<{
  children: React.ReactNode;
  leadUuid?: string;
  type?: "LEAD" | "MASTER";
}> = ({ children, leadUuid, type }) => {
  const dispatch = useDispatch();
  const companyOutlet = useBillingCompanyContext();
  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;

  const { data: categories_list } = useSelector(
    (storeState: IStoreState) => storeState.food.foodCategory,
  );

  const [categorySearchText, setCategorySearchText] = useState("");
  const [filteredCategories, setFilteredCategories] = useState<
    IFoodMenuCategory[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    category: IFoodMenuCategory | null;
    sub_category: ISubCategoriesJson | null;
    food_items: IFoodItem[];
  }>({
    category: null,
    sub_category: null,
    food_items: [],
  });

  const [selectedItems, setSelectedItems] = useState<IFoodItem[]>([]);
  const [fetchedItems, setFetchedItems] = useState<IFoodItem[]>([]);
  const [masterMenusFetchedItems, setMasterMenusFetchedItems] = useState<
    IFoodItem[]
  >([]);
  const [deletedItemUuids, setDeletedItemUuids] = useState<Set<string>>(
    new Set(),
  );
  const [categoryLimits, setCategoryLimits] = useState<Record<string, number>>(
    {},
  );
  const [menuPrice, setMenuPrice] = useState<number>(0);

  React.useEffect(() => {
    if (categories_list.length > 0 && !selectedCategory) {
      setSelectedCategory({
        category: categories_list[0],
        sub_category: null,
        food_items: categories_list[0].food_items,
      });
    }
  }, [categories_list, selectedCategory, setSelectedCategory]);

  React.useEffect(() => {
    if (!company_outlet_uuid) return;
    if (type === "MASTER") {
      dispatch(
        fetchFoodMenuCategoryListWithFoodItemsAsync({
          company_outlet_uuid,
          page: 1,
          rowsPerPage: 100,
          pageLimit: 100,
          status: "ACTIVE",
        }) as any,
      );
    }
  }, [company_outlet_uuid, dispatch, type]);

  React.useEffect(() => {
    if (categorySearchText.length > 0) {
      const filtered = categories_list.filter((item) =>
        item.category_name
          .toLowerCase()
          .includes(categorySearchText.toLowerCase()),
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories_list);
    }
  }, [categorySearchText, categories_list]);

  const addDeletedItemUuid = (uuid: string) => {
    setDeletedItemUuids((prev) => {
      const newSet = new Set(prev);
      newSet.add(uuid);
      return newSet;
    });
  };

  const removeFetchedItem = (itemUuid: string) => {
    setFetchedItems((prev) =>
      prev.filter((item) => item.food_item_uuid !== itemUuid),
    );
    setMasterMenusFetchedItems((prev) =>
      prev.filter((item) => item.food_item_uuid !== itemUuid),
    );
  };

  const handleSetSelectedCategory = (category: IFoodMenuCategory | null) => {
    setSelectedCategory({
      category: category,
      sub_category: null,
      food_items: category?.food_items || [],
    });
  };

  const handleSetSelectedSubCategory = (
    category: IFoodMenuCategory,
    subCategory: ISubCategoriesJson | null,
  ) => {
    if (subCategory) {
      setSelectedCategory({
        category: category,
        sub_category: subCategory,
        food_items: subCategory.food_items || [],
      });
    } else {
      setSelectedCategory({
        category: category,
        sub_category: null,
        food_items: [],
      });
    }
  };

  const addItemToOrder = (item: IFoodItem) => {
    const categoryUuid = item.category_list?.[0]?.category_uuid;
    if (categoryUuid) {
      const categoryLimit = categoryLimits[categoryUuid];
      if (categoryLimit !== undefined) {
        const currentCategoryCount = selectedItems.filter(
          (i) => i.category_list?.[0]?.category_uuid === categoryUuid,
        ).length;
        if (currentCategoryCount >= categoryLimit) {
          return;
        }
      }
    }

    const subCategoryUuid = item.sub_category_uuid;
    if (subCategoryUuid) {
      const subCategoryLimit = categoryLimits[subCategoryUuid];
      if (subCategoryLimit !== undefined) {
        const currentSubCount = selectedItems.filter(
          (i) => i.sub_category_uuid === subCategoryUuid,
        ).length;
        if (currentSubCount >= subCategoryLimit) {
          return;
        }
      }
    }

    const itemWithCategory = {
      ...item,
      is_selected: item.is_selected !== undefined ? item.is_selected : 1,
      is_show: item.is_show !== undefined ? item.is_show : 1,
      category_list: selectedCategory.category
        ? [selectedCategory.category]
        : [],
    };
    setSelectedItems((prevItems) => [...prevItems, itemWithCategory]);
  };

  const removeItemFromOrder = (itemUuid: string) => {
    setSelectedItems((prevItems) =>
      prevItems.filter((item) => item.food_item_uuid !== itemUuid),
    );
  };

  const updateItemQuantity = (itemUuid: string, quantity: number) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.food_item_uuid === itemUuid ? { ...item, quantity } : item,
      ),
    );
  };

  const updateItemSelectionShow = (itemUuid: string, isSelectedVal: number, isShow: number) => {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.food_item_uuid === itemUuid
          ? { ...item, is_selected: isSelectedVal, is_show: isShow }
          : item
      )
    );
    setFetchedItems((prev) =>
      prev.map((item) =>
        item.food_item_uuid === itemUuid
          ? { ...item, is_selected: isSelectedVal, is_show: isShow }
          : item
      )
    );
    setMasterMenusFetchedItems((prev) =>
      prev.map((item) =>
        item.food_item_uuid === itemUuid
          ? { ...item, is_selected: isSelectedVal, is_show: isShow }
          : item
      )
    );
  };

  const clearOrder = () => {
    setSelectedItems([]);
  };

  const mergeItemsFromMultipleMenus = (itemsArrays: IFoodItem[][]) => {
    const mergedItems: IFoodItem[] = [];
    const seenUuids = new Set<string>();

    itemsArrays.forEach((items) => {
      items.forEach((item) => {
        if (item.food_item_uuid && !seenUuids.has(item.food_item_uuid)) {
          seenUuids.add(item.food_item_uuid);
          mergedItems.push(item);
        }
      });
    });

    return mergedItems;
  };

  React.useEffect(() => {
    if (leadUuid) {
      setFetchedItems([]);
      setSelectedItems([]);

      setSelectedCategory({
        category: null,
        sub_category: null,
        food_items: [],
      });

      setDeletedItemUuids(new Set());
      // setCategoryLimits({});
      setMenuPrice(0);
    }
  }, [leadUuid]);

  return (
    <OrderContext.Provider
      value={{
        allCategories: filteredCategories,
        selectedCategory: selectedCategory.category,
        onCategorySelect: handleSetSelectedCategory,
        selectedSubCategory: selectedCategory.sub_category,
        onSubCategorySelect: handleSetSelectedSubCategory,
        categorySearchText,
        setCategorySearchText,

        selectedItems,
        addItemToOrder,
        removeItemFromOrder,
        updateItemQuantity,
        clearOrder,
        currentCategoryItems: selectedCategory.food_items,
        fetchedItems,
        setFetchedItems,
        masterMenusFetchedItems,
        setMasterMenusFetchedItems,
        removeFetchedItem,
        deletedItemUuids,
        addDeletedItemUuid,
        mergeItemsFromMultipleMenus,
        categoryLimits,
        setCategoryLimits,
        menuPrice,
        setMenuPrice,
        updateItemSelectionShow,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
